import { useRouter } from 'next/navigation';

import { MESSAGES_PER_PAGE } from '@/constants/common';
import {
  getChatByIdQuery,
  getChatQuery,
  getUserChatsQuery
} from '@/graphql/chat/queries';
import { createMessageMutation } from '@/graphql/message/mutations';
import { getMessagesQuery } from '@/graphql/message/queries';
import {
  CreateMessageResponse,
  CreateMessageVariables
} from '@/graphql/types/inputs/createMessage';
import { MessagePayload } from '@/graphql/types/inputs/messageInput.type';
import { Listing } from '@/graphql/types/listingInput';
import { GetMessagesResponse } from '@/graphql/types/results/getMessages';
import {
  GetChatByIdResponse,
  GetChatResponse,
  GetUserChatsResponse
} from '@/graphql/types/results/getUserChats';
import { useChat } from '@/providers/ChatProvider';
import { Message, TemporaryMessage } from '@/types/chat';
import { User } from '@/types/user';
import {
  executeMutationWithJWT,
  executeQueryWithJWT
} from '@/utils/helpers/graphql';

export const useChatActions = () => {
  const {
    updateMessageError,
    replaceMessage,
    replaceChannelById,
    upsertChannel,
    addChannels,
    addMessages,
    getChannel,
    addMessage,
    updateLastMessage,
    selectedChannel
  } = useChat();
  const router = useRouter();

  const getChat = async (
    storeId: string,
    listingId?: string,
    receiverId?: string
  ) => {
    const response = await executeQueryWithJWT<GetChatResponse>(getChatQuery, {
      listingId,
      receiverId
    });
    const chat = response.data.getChat;

    if (chat) {
      if (storeId.startsWith('temporary.')) {
        replaceChannelById(storeId, chat);
      } else {
        upsertChannel(chat);
      }

      return chat;
    }

    return null;
  };

  const getChatById = async (storeId: string) => {
    const response = await executeQueryWithJWT<GetChatByIdResponse>(
      getChatByIdQuery,
      {
        chatId: storeId
      }
    );
    const chat = response?.data.getChatById;

    if (chat) {
      upsertChannel(chat);

      return chat;
    }

    return null;
  };

  const getUserChats = async ({
    nextToken,
    limit = 20,
    loadMore = false
  }: {
    nextToken: string | null;
    limit?: number;
    loadMore?: boolean;
  }) => {
    const response = await executeQueryWithJWT<GetUserChatsResponse>(
      getUserChatsQuery,
      {
        limit,
        nextToken: loadMore ? nextToken : null
      }
    );
    const chats = response?.data?.getUserChats;

    if (chats.items) {
      addChannels(chats.items);
    }

    return chats;
  };

  const sendMessage = async (
    chatId: string,
    message: Message | TemporaryMessage,
    isNewChat: boolean,
    channelProperties: {
      listing: Listing;
      receiver: User;
    }
  ) => {
    try {
      const { listing, receiver } = channelProperties;

      if (isNewChat && (!receiver || !listing)) {
        return;
      }

      const messageInput = isNewChat
        ? {
            chatInput: {
              createUserInput: {
                userId: receiver.id,
                name: receiver.name,
                photo: receiver.photo,
                phones: receiver.phones
              },
              listingInput: listing
            }
          }
        : { chatId };
      const newMessage = await executeMutationWithJWT<
        CreateMessageResponse,
        CreateMessageVariables
      >(createMessageMutation, {
        input: {
          text: message.text,
          ...messageInput
        }
      });

      if (newMessage?.createMessage) {
        replaceMessage(chatId, message, newMessage?.createMessage);

        if (isNewChat && newMessage?.createMessage.chatId) {
          const fullChat = await getChat(chatId, listing?.id, receiver?.id);

          if (fullChat) {
            router.replace(`/conversation/${fullChat.id}`);
          }
        }
      } else {
        updateMessageError(chatId, message, true);
      }
    } catch (e) {
      updateMessageError(chatId, message, true);
      console.log(e);
    }
  };

  const getMessages = async (nextToken: string | null, chatId?: string) => {
    if (!chatId) return;

    const response = await executeQueryWithJWT<GetMessagesResponse>(
      getMessagesQuery,
      {
        chatId,
        limit: MESSAGES_PER_PAGE,
        nextToken
      }
    );
    const messages = response.data.getMessages;

    if (messages.items) {
      addMessages(chatId, messages.items);
    }

    return messages;
  };

  const onMessageReceived = async (msg: MessagePayload) => {
    const existingChat = getChannel(msg.chatId);

    if (existingChat) {
      if (selectedChannel && selectedChannel.id === msg.chatId) {
        addMessage(msg.chatId, msg);
      } else {
        updateLastMessage(msg.chatId, msg);
      }
    } else {
      await getChatById(msg.chatId);
    }
  };

  return {
    getChat,
    getChatById,
    getUserChats,
    sendMessage,
    getMessages,
    onMessageReceived
  };
};
